import axios from 'axios';
import { serialize } from 'object-to-formdata';
import fileDownload from 'js-file-download';
import moment from 'moment/moment';
import _ from 'lodash';
import apiConfig from '../config';
import { socketDestroy, healthPlanSocketInit } from '../store/actions/chat';
import accountHelpers from '../utils/accountHelpers';
import { rightChatSocketDestroy, socketInit } from '../store/actions/rightBar';

const { API_URL } = apiConfig;

const tokenRefresh = async () => {
  const refreshToken = accountHelpers.getToken('refreshToken');
  const { email, username } = accountHelpers.getUser();

  if (refreshToken && (email || username)) {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/user/token/refresh`,
        {
          username: email || username,
          refreshToken,
        },
        { headers: { Authorization: `Bearer ${refreshToken}` } },
      );

      if (data?.token) {
        accountHelpers.setToken(data.token);

        const userId = accountHelpers.getDecodedToken()?.id;
        const payload = await Api.getAccount(userId);
        accountHelpers.setUser(payload.data.data.response[0]);

        if (accountHelpers.isLocked()) {
          localStorage.setItem(
            'error',
            `Account locked due to too many wrong password tries. Try agian after ${accountHelpers.getLockDate()
              .format('YYYY-MM-DD HH:mm:ss')}`,
          );

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else if (!accountHelpers.isActive()) {
          accountHelpers.removeLocalStorage();

          localStorage.setItem(
            'error',
            'This account is not active. Please ask an administrator for more information.',
          );

          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else {
          window.store.dispatch(socketInit(data.token));
          // window.store.dispatch(healthPlanSocketInit(data.token));
        }
      }
    } catch (e) {
      console.warn(e);
      throw e;
    }
  } else {
    accountHelpers.removeLocalStorage();
    window.location.href = '/login';

    throw new Error('no user info');
  }
};

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'skip-browser-warning',
  },
});

api.interceptors.request.use(
  async (config) => {
    let token = accountHelpers.getToken();

    if (token) {
      const expiresAt = accountHelpers.getDecodedToken()?.exp;

      if (new Date(expiresAt * 1000) <= new Date()) {
        try {
          // window.store.dispatch(socketDestroy());
          window.store.dispatch(rightChatSocketDestroy());
          await tokenRefresh();
          token = accountHelpers.getToken();
        } catch (err) {
          return Promise.reject(err);
        }
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use((response) => response, (error) => {
  if (error.response && error.response.status === 401) {
    if (window.location.pathname !== '/login') {
      accountHelpers.removeLocalStorage();

      window.location.href = '/login';
    }
  }

  return Promise.reject(error);
});

const apiTokens = {};

class Api {
  static cancel(methodName) {
    if (apiTokens[methodName]) {
      apiTokens[methodName]();
    }
  }

  static cancelToken(methodName) {
    return new axios.CancelToken((token) => {
      apiTokens[methodName] = token;
    });
  }

  static login(data) {
    return api.post(`${API_URL}/api/user/login`, data);
  }

  static loginAdminToHealth({ id }) {
    return api.post(`${API_URL}/api/user/login/admin`, { id });
  }

  static changePassword(data) {
    return api.post(`${API_URL}/api/account/chpass`, data);
  }

  static setNewPassword(data) {
    return api.post(`${API_URL}/api/account/respass`, data);
  }

  static setNewPasswordAdmin(data) {
    return api.post(`${API_URL}/api/account/change`, data);
  }

  static getAccount(userId) {
    return api(`${API_URL}/api/user/search?userId=${userId}`);
  }

  static getUserList({
    role, limit, page = 1, email, username, name, searcherId, userRole,
    userId, groupName, healthId, access, groupId, active, activationRequest,
  }) {
    return api(`${API_URL}/api/user/search`, {
      params: {
        role,
        limit,
        page,
        email,
        username,
        name,
        searcherId,
        userRole,
        userId,
        groupName,
        healthId,
        groupId,
        access,
        active,
        activationRequest,
      },
    });
  }

  static getDashboardStats({
    start, end, rep, healthPlan, pdcMode,
  }) {
    return api(`${API_URL}/api/dashboards`, {
      params: {
        start, end, rep, healthPlan, pdcMode,
      },
    });
  }

  static getDashboardChart({
    start, end, range, rep,
  }) {
    return api(`${API_URL}/api/dashboards/chart`, {
      params: {
        start, end, range, rep,
      },
    });
  }

  static getAdherenceChart({
    start, end, rep, healthPlan, pdcMode,
  }) {
    this.cancel('getAdherenceChart');

    return api(`${API_URL}/api/dashboards/adherence`, {
      cancelToken: this.cancelToken('getAdherenceChart'),
      params: {
        start, end, rep, healthPlan, pdcMode,
      },
    });
  }

  static getTimeToTherapyChart({
    start, end, rep, healthPlan, pdcMode,
  }) {
    this.cancel('getTimeToTherapyChart');

    return api(`${API_URL}/api/dashboards/time-to-therapy`, {
      cancelToken: this.cancelToken('getTimeToTherapyChart'),
      params: {
        start, end, rep, healthPlan, pdcMode,
      },
    });
  }

  static getMPRTrendChart({
    start, end, rep, healthPlan, pdcMode,
  }) {
    this.cancel('getMPRTrendChart');

    return api(`${API_URL}/api/dashboards/mpr-trend`, {
      cancelToken: this.cancelToken('getMPRTrendChart'),
      params: {
        start, end, rep, healthPlan, pdcMode,
      },
    });
  }

  static getPayorMixChart({
    start, end, rep, healthPlan,
  }) {
    this.cancel('getPayorMixChart');

    return api(`${API_URL}/api/dashboards/payor-mix`, {
      cancelToken: this.cancelToken('getPayorMixChart'),
      params: {
        start, end, rep, healthPlan,
      },
    });
  }

  static getPatientAdherence({
    start, end, rep, page, limit, healthPlan, pdcMode,
  }) {
    this.cancel('getPatientAdherence');

    return api(`${API_URL}/api/dashboards/patient-adherence`, {
      cancelToken: this.cancelToken('getPatientAdherence'),
      params: {
        start, end, rep, page, limit, healthPlan, pdcMode,
      },
    });
  }

  static getQuickStats({
    start, end, rep, healthPlan,
  }) {
    this.cancel('getQuickStats');

    return api(`${API_URL}/api/dashboards/quick-stats`, {
      cancelToken: this.cancelToken('getQuickStats'),
      params: {
        start, end, rep, healthPlan,
      },
    });
  }

  static getRecentInterventions({
    start, end, rep, healthPlan,
  }) {
    this.cancel('getRecentInterventions');

    return api(`${API_URL}/api/dashboards/recent-interventions`, {
      cancelToken: this.cancelToken('getRecentInterventions'),
      params: {
        start, end, rep, healthPlan,
      },
    });
  }

  static getDoseModifications({
    start, end, rep, healthPlan,
  }) {
    this.cancel('getDoseModifications');

    return api(`${API_URL}/api/dashboards/dose-modifications`, {
      cancelToken: this.cancelToken('getDoseModifications'),
      params: {
        start, end, rep, healthPlan,
      },
    });
  }

  static getCmsStarRatings({
    start, end, rep, healthPlan,
  }) {
    this.cancel('getCmsStarRatings');

    return api(`${API_URL}/api/dashboards/cms-star-ratings`, {
      cancelToken: this.cancelToken('getCmsStarRatings'),
      params: {
        start, end, rep, healthPlan,
      },
    });
  }

  static getMedicationCoverage({
    start, end, rep, healthPlan,
  }) {
    this.cancel('getMedicationCoverage');

    return api(`${API_URL}/api/dashboards/medication-coverage`, {
      cancelToken: this.cancelToken('getMedicationCoverage'),
      params: {
        start, end, rep, healthPlan,
      },
    });
  }

  static getDashboardTopPhysicians({
    start, end, rep, healthPlan, page = 1, limit, cancelKey = 'getDashboardTopPhysicians',
  }) {
    this.cancel(cancelKey);

    return api(`${API_URL}/api/dashboards/top-physicians`, {
      cancelToken: this.cancelToken(cancelKey),
      params: {
        start, end, rep, healthPlan, page, limit,
      },
    });
  }

  static getDashboardTopMedications({
    start, end, rep, healthPlan, page = 1, limit, cancelKey = 'getDashboardTopMedications',
  }) {
    this.cancel(cancelKey);

    return api(`${API_URL}/api/dashboards/top-medications`, {
      cancelToken: this.cancelToken(cancelKey),
      params: {
        start, end, rep, healthPlan, page, limit,
      },
    });
  }

  static getDashboardGrowthBySpecializations({
    product, state, rep, start, end,
  }) {
    return api(`${API_URL}/api/dashboards/report/growth`, {
      params: {
        product, state, rep, start, end,
      },
    });
  }

  static async getDashboardSyncPakVsSortPak({
    start, end, state, key, pouch, health, download,
  }) {
    const {
      data, totalSortPak, totalSyncPak,
    } = await api(`${API_URL}/api/dashboards/report/pouch`, {
      params: {
        start, end, state, key, pouch, health, download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      fileDownload(data, 'SyncPak_vs_SortPak.xlsx');
    }

    return { data, totalSortPak, totalSyncPak };
  }

  static getDashboardRepScorecard({
    start, end, page, limit = 50,
  }) {
    return api(`${API_URL}/api/dashboards/report/rep`, {
      params: {
        start, end, page, limit,
      },
    });
  }

  static getDashboardRepPayment({ start, end, name }) {
    return api(`${API_URL}/api/dashboards/report/rep-payment`, {
      params: {
        start, end, name,
      },
    });
  }

  static getDashboardRepInvoices({
    start, end, name, status,
  }) {
    return api(`${API_URL}/api/dashboards/report/rep-invoice`, {
      params: {
        start, end, name, status,
      },
    });
  }

  static payBonus({
    userId, date, amount, paymentMethod, checkNumber,
  }) {
    return api.post(`${API_URL}/api/user/pay`, {
      userId, date, amount, paymentMethod, checkNumber,
    });
  }

  static deleteInvoice({ id, userId, date }) {
    return api.delete(`${API_URL}/api/user/pay`, { data: { id, userId, date } });
  }

  static getDashboardRepSchedule({
    start, end, page, limit = 50, rep,
  }) {
    return api(`${API_URL}/api/dashboards/report/schedule`, {
      params: {
        start, end, page, limit, rep,
      },
    });
  }

  static async getDashboardUserWorkflow({ start, end, download }) {
    const { data } = await api(`${API_URL}/api/dashboards/report/workflow`, {
      params: {
        start,
        end,
        download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      fileDownload(data, 'Report.xlsx');
    }

    return data;
  }

  static async getDashboardScriptWorkflow({ start, end, download }) {
    const { data } = await api(`${API_URL}/api/dashboards/report/script-workflow`, {
      params: {
        start,
        end,
        download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      fileDownload(data, 'ScriptWorkflowReport.xlsx');
    }

    return data;
  }

  static async getDashboardNoteCount({ start, end }) {
    const { data } = await api(`${API_URL}/api/dashboards/report/notes`, { params: { start, end } });

    return data;
  }

  static async getDashboardAutofillStats({
    healthUserId, start, end, sort, sortBy, page = 1, limit,
  }) {
    return api(`${API_URL}/api/dashboards/autofill-stats`, {
      params: {
        healthUserId, start, end, sort, sortBy, page, limit,
      },
    });
  }

  static getDashboardNewPhysician({
    start, end, page, limit, product, specialization,
  }) {
    return api(`${API_URL}/api/dashboards/report/new-physician`, {
      params: {
        start, end, page, limit, product, specialization,
      },
    });
  }

  static searchProduct(search) {
    return api(`${API_URL}/api/products/search`, {
      params: {
        search,
        limit: 100,
      },
    });
  }

  static updateProduct(data) {
    return api.put(`${API_URL}/api/products/update`, data);
  }

  static getTopMedications({
    limit, page, zip, product, specialization, end, start, rep,
  }) {
    return api(`${API_URL}/api/dashboards/report/medication`, {
      params: {
        limit, page, zip, product, specialization, end, start, rep,
      },
    });
  }

  static getTopCities({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/city`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopState({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/state`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopSpecializations({
    limit, page, rep, zip, product, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/specialization`, {
      params: {
        limit, page, rep, zip, product, end, start,
      },
    });
  }

  static getTopTopCopayNetworks({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/copay`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopPayors({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/payor`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopHubs({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/hub`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopPhysicians({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/physician`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopPhysiciansByRevenue({
    limit, page, rep, state, ids, zip, product, productId, specialization, months,
  }) {
    return api(`${API_URL}/api/dashboards/report/physician-revenue`, {
      params: {
        limit, page, rep, state, ids, zip, product, productId, specialization, months,
      },
    });
  }

  static getPhysiciansByRevenue({
    limit, page, rep, state, ids, zip, product, productId, specialization, months,
  }) {
    return api(`${API_URL}/api/dashboards/report/physician-search`, {
      params: {
        limit, page, rep, state, ids, zip, product, productId, specialization, months,
      },
    });
  }

  static getUnassignedPhysicians({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/phys-blank`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getTopGroups({
    limit, page, rep, zip, product, specialization, end, start,
  }) {
    return api(`${API_URL}/api/dashboards/report/group`, {
      params: {
        limit, page, rep, zip, product, specialization, end, start,
      },
    });
  }

  static getCelmod({
    page, limit, start, end, physicianId,
  }) {
    return api(`${API_URL}/api/dashboards/report/celmod`, {
      params: {
        page, limit, start, end, physicianId,
      },
    });
  }

  static getCelmodTop({ start, end, physicianId }) {
    return api(`${API_URL}/api/dashboards/report/celmod-top`, {
      params: { start, end, physicianId },
    });
  }

  static getPhysicianCelmod({ page, limit, physicianId }) {
    return api(`${API_URL}/api/physicians/celmod/${physicianId}`, {
      // params: { page, limit },
    });
  }

  static addCelmod({
    active, therapy, relapse, therapySwitch, PhysicianId,
  }) {
    return api.post(`${API_URL}/api/physicians/celmod/add`, serialize({
      active, therapy, relapse, therapySwitch, PhysicianId,
    }));
  }

  static getGrowthManufacturer(date) {
    return api(`${API_URL}/api/dashboards/report/growth-manufacturer`, {
      params: { date },
    });
  }

  static getPhysicianMedication({
    physicianId, medicationId, type, dateType, mode, date1, date2,
  }) {
    return api(`${API_URL}/api/dashboards/report/physician-medication`, {
      params: {
        physicianId, medicationId, type, dateType, mode, date1, date2,
      },
    });
  }

  static getManufacturerUserReport(params) {
    return api(`${API_URL}/api/dashboards/report/manufacturer`, { params });
  }

  static getReportSearchList({
    group, diagnosis, location, payor,
  }) {
    return api(`${API_URL}/api/dashboards/select`, {
      params: {
        group, diagnosis, location, payor,
      },
    });
  }

  static getScripts({
    status, location, salesCode, homeCare, textSearch, limit, rep, assign,
    specialization, userId, page, patientId, scriptId, physicianId, healthId, patientLocation, pouch, download,
    cancelToken,
  }) {
    if (cancelToken) this.cancel(cancelToken);

    return api(`${API_URL}/api/scripts/search`, {
      cancelToken: cancelToken ? this.cancelToken(cancelToken) : undefined,
      params: {
        status,
        location,
        salesCode,
        homeCare,
        textSearch,
        limit,
        rep,
        specialization,
        userId,
        page,
        patientId,
        scriptId,
        physicianId,
        assign,
        healthId,
        patientLocation,
        pouch,
        download,
      },
    });
  }

  static addScript(data) {
    return api.post(`${API_URL}/api/scripts/add`, data);
  }

  static deleteScript(id) {
    return api.delete(`${API_URL}/api/scripts/delete/${id}`);
  }

  static updateScript(data) {
    return api.put(`${API_URL}/api/scripts/update`, data);
  }

  static updateScriptStatuses(data) {
    return api.post(`${API_URL}/api/scripts/statuses/add`, data);
  }

  static updateScriptChecklist(data, signed) {
    return api.put(`${API_URL}/api/products/checklist/update`, data, {
      params: { signed },
    });
  }

  static signScriptChecklist({
    id, ScriptId, ProductId, attachmentFile,
  }) {
    return api.post(`${API_URL}/api/products/checklist/sign`, serialize({
      id, ScriptId, ProductId, attachmentFile,
    }));
  }

  static async downloadScriptChecklist(id) {
    const { data, headers } = await api.get(`${API_URL}/api/products/checklist/download/${id}`, {
      responseType: 'blob',
    });

    const [, fileName = 'Checklist.pdf'] = (headers['content-disposition'] || '').match(/\s+filename=(.+)$/) || [];

    fileDownload(data, fileName);
  }

  static getMessages({ page }) {
    return api(`${API_URL}/api/chat`, {
      params: { page },
    });
  }

  static setMessage({ message, mention, receiverId = null }) {
    return api.post(`${API_URL}/api/chat`, { message, mention, receiverId });
  }

  static getNotifications(page) {
    return api(`${API_URL}/api/chat/note`, {
      params: { page, limit: 20 },
    });
  }

  static getNotificationCount() {
    return api(`${API_URL}/api/chat/note`);
  }

  static async getPatients({
    page = 1, limit, userId, name, dob, address, phone, patientId, ids, download,
  }) {
    const response = await api(`${API_URL}/api/patients/search`, {
      params: {
        page, limit, userId, name, dob, address, phone, patientId, ids, download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      const [, fileName] = (response.headers['content-disposition'] || '').match(/\s+filename=(.+)$/)
      || ['', 'Patients.xlsx'];

      fileDownload(response.data, fileName);
    }

    return response;
  }

  static sentNewUser({
    name, email, username, role, active, lastName, dob, zip, phone, medicalId, manufacturerIds,
  }) {
    return api.post(`${API_URL}/api/account/register`, {
      name, email, username, role, active, lastName, dob, zip, phone, medicalId, manufacturerIds,
    });
  }

  static sentEditUser({
    name, email, username, active, id, role, lastName, dob, lockedDateTimeUTC, lockedCount, manufacturerIds,
  }) {
    return api.put(`${API_URL}/api/user/update`, {
      name, email, username, active, id, role, lastName, dob, lockedDateTimeUTC, lockedCount, manufacturerIds,
    });
  }

  static getReqChangePassword({ userId, targetUserId, callbackUrl }) {
    return api.get(`${API_URL}/api/account/respass`, {
      params: {
        userId, targetUserId, callbackUrl,
      },
    });
  }

  static sentEditProfileUser(data) {
    return api.put(`${API_URL}/api/user/update`, serialize(data));
  }

  static mergeScripts({ oldPatientId, patientId }) {
    return api.put(`${API_URL}/api/scripts/merge`, { oldPatientId, patientId });
  }

  static mergePhysicianScripts({ oldPhysicianId, physicianId, userId }) {
    return api.put(`${API_URL}/api/scripts/mergephys`, { oldPhysicianId, physicianId, userId });
  }

  static deletePatient({ patientId, userId }) {
    return api.delete(`${API_URL}/api/patients/delete`, { data: { patientId, userId } });
  }

  static createPatient(data) {
    return api.post(`${API_URL}/api/patients/add`, data);
  }

  static bulkCreatePatient(data) {
    return api.post(`${API_URL}/api/patients/bulk`, serialize(data));
  }

  static importIehpActionTracker(data) {
    return api.post(`${API_URL}/api/import/iehp-action-tracker`, serialize(data));
  }

  static updatePatient(data) {
    return api.put(`${API_URL}/api/patients/update`, data);
  }

  static notifyPatient(data) {
    return api.post(`${API_URL}/api/patients/notify-patient`, data);
  }

  static getNotifyPatientHistory({ patientId }) {
    return api.get(`${API_URL}/api/patients/notify-patient/${patientId}/history`);
  }

  static updatePatientSmsOptOut({ patientId, value }) {
    return api.put(`${API_URL}/api/patients/${patientId}/sms-opt-out`, { value });
  }

  static getPastInsurance({ patientId }) {
    return api.get(`${API_URL}/api/pastInsurance/search`, {
      params: { patientId },
    });
  }

  static getPatientAttachments({ patientId }) {
    return api.get(`${API_URL}/api/patientAttachments/search`, {
      params: { patientId },
    });
  }

  static sendInsuranceFile({
    attachmentFile, fileName, patientId, attachmentId, attachedBy, scriptId, faxes, formData,
  }) {
    return api.put(`${API_URL}/api/sthree/put`, formData || serialize({ attachmentFile }), {
      params: {
        fileName, patientId, attachmentId, attachedBy, scriptId, faxes,
      },
    });
  }

  static uploadInsuranceFile({
    type, attachmentFile, fileName, patientId, userId, attachedBy,
  }) {
    return api.post(`${API_URL}/api/patientAttachments/upload`, serialize({ attachmentFile }), {
      params: {
        type, fileName, patientId, userId, attachedBy,
      },
    });
  }

  static getFile({
    fileName, patientId, userId, attachmentId, scriptId,
  }, onDownloadProgress) {
    return api.get(`${API_URL}/api/sthree/get`, {
      params: {
        fileName, patientId, userId, attachmentId, scriptId,
      },
      onDownloadProgress,
    });
  }

  static getPatientsNode({ patientId, userId }) {
    return api.get(`${API_URL}/api/patients/notes/search`, {
      params: { patientId, userId },
    });
  }

  static addPatientNode(data) {
    return api.post(`${API_URL}/api/patients/notes/add`, data);
  }

  static getRefills({
    status, textSearch, rep, spec, page, limit, userId, start, end, hub,
  }) {
    return api.get(`${API_URL}/api/scripts/refills/search`, {
      params: {
        status, textSearch, rep, spec, page, limit, userId, start, end, hub,
      },
    });
  }

  static getScriptsNotes({
    scriptId, userId,
  }) {
    return api.get(`${API_URL}/api/scripts/notes/search`, {
      params: { scriptId, userId },
    });
  }

  static addScriptNote(data) {
    return api.post(`${API_URL}/api/scripts/notes/add`, data);
  }

  static editScriptNote({ noteId, ...data }) {
    return api.put(`${API_URL}/api/scripts/notes/${noteId}`, data);
  }

  static deleteScriptNote({ noteId }) {
    return api.delete(`${API_URL}/api/scripts/notes/${noteId}`);
  }

  static addScriptAttachmentCopy(data) {
    return api.post(`${API_URL}/api/attachments/copy`, data);
  }

  static addScriptNoteCopy(data) {
    return api.post(`${API_URL}/api/scripts/notes/copy`, data);
  }

  static getScriptStatus({ ScriptId }) {
    return api.get(`${API_URL}/api/scripts/statuses/search/`, {
      params: { ScriptId },
    });
  }

  static getScriptPayments({ scriptId }) {
    return api.get(`${API_URL}/api/scripts/payments/search`, {
      params: { scriptId },
    });
  }

  static getScriptPaymentsPdf({ fileName, receiptId, userId }, onDownloadProgress) {
    return api.get(`${API_URL}/api/sthree/get`, {
      params: { fileName, receiptId, userId },
      onDownloadProgress,
    });
  }

  static getScriptAttachments({ ScriptId, userId, type }) {
    return api.get(`${API_URL}/api/attachments/search`, {
      params: { ScriptId, userId, type },
    });
  }

  static uploadAttachmentFile({
    userId, scriptId, fileName, type, attachedBy, attachmentFile,
  }) {
    return api.post(`${API_URL}/api/attachments/upload`, serialize({ attachmentFile }), {
      params: {
        userId, scriptId, fileName, type, attachedBy,
      },
    });
  }

  static scriptCheckout({ userId, statuses, patientId }) {
    return api.post(`${API_URL}/api/scripts/checkout`, { userId, statuses, patientId });
  }

  static paymentCharge(data) {
    return api.post(`${API_URL}/api/scripts/payments/charge/card`, data);
  }

  static paymentChargeCustomer(data) {
    return api.post(`${API_URL}/api/scripts/payments/charge/customer`, data);
  }

  static uploadToSthree(data, fileName, receiptId) {
    return api.put(`${API_URL}/api/sthree/put?fileName=${fileName}&receiptId=${receiptId}`, data);
  }

  static getPhysicians({
    searchSpec, phone, address, searchGroup, name, limit, page, userId, physicianId, revenue, group, physGroup, rep,
    repId,
  }) {
    return api.get(`${API_URL}/api/physicians/search`, {
      params: {
        searchSpec,
        phone,
        address,
        searchGroup,
        name,
        limit,
        page,
        userId,
        physicianId,
        revenue,
        group,
        physGroup,
        rep,
        repId,
      },
    });
  }

  static getCelmodPhysicians({ name }) {
    return api.get(`${API_URL}/api/physicians/search/celmod`, {
      params: { name },
    });
  }

  static createPhysician(data) {
    return api.post(`${API_URL}/api/physicians/add`, data);
  }

  static updatePhysician(data) {
    return api.put(`${API_URL}/api/physicians/update`, data);
  }

  static deletePhysician({ physicianId }) {
    return api.delete(`${API_URL}/api/physicians/delete`, { data: { physicianId } });
  }

  static getPhysicianNotes({ physicianId }) {
    return api.get(`${API_URL}/api/visits/notes/search`, {
      params: { physicianId },
    });
  }

  static getPhysicianVisits({
    physicianId, group, date, endDate,
  }) {
    return api.get(`${API_URL}/api/visits/search`, {
      params: {
        physicianId, group, date, endDate,
      },
    });
  }

  static getRepLocation({ userId, date }) {
    return api.get(`${API_URL}/api/visits/location`, {
      params: { userId, date },
    });
  }

  static getRepLocationDaily({ userId, date }) {
    return api.get(`${API_URL}/api/visits/location-daily`, {
      params: { userId, date },
    });
  }

  static addVisit(data) {
    return api.post(`${API_URL}/api/visits/add`, data);
  }

  static editVisit(data) {
    return api.put(`${API_URL}/api/visits/update`, data);
  }

  static deleteVisit(id) {
    return api.delete(`${API_URL}/api/visits/delete`, { data: { id } });
  }

  static addPhysicianNote(data) {
    return api.post(`${API_URL}/api/visits/notes/add`, data);
  }

  static addPhysicianAccess(data) {
    return api.post(`${API_URL}/api/user/userPhysicians`, data);
  }

  static deletePhysicianAccess(data) {
    return api.delete(`${API_URL}/api/user/userPhysicians/delete`, { data });
  }

  static getPhysicianGroupNotes({ group }) {
    return api.get(`${API_URL}/api/groupNotes/search`, {
      params: { group },
    });
  }

  static addPhysicianGroupNote(data) {
    return api.post(`${API_URL}/api/groupNotes/add`, data);
  }

  static getPhysicianGroupUsers({ group }) {
    return api.get(`${API_URL}/api/physicians/tab`, {
      params: { group },
    });
  }

  static getProducts({
    page, limit, search, productId, otc, sort, sortBy, status, brand,
  }) {
    return api.get(`${API_URL}/api/products/search`, {
      params: {
        page, limit, search, productId, otc, sort, sortBy, status, brand,
      },
    });
  }

  static addProduct(data) {
    return api.post(`${API_URL}/api/products/add`, data);
  }

  static upsertProduct(data) {
    return api.post(`${API_URL}/api/products/upsert`, data);
  }

  static editProduct(data) {
    return api.put(`${API_URL}/api/products/update`, data);
  }

  static editProductJCode({ id, ...data }) {
    return api.put(`${API_URL}/api/products/${id}/jcode`, data);
  }

  static productUploadXLSXFile({
    sheet, name, ndc, quantity, price,
  }) {
    return api.post(`${API_URL}/api/products/upload`, serialize({
      sheet, name, ndc, quantity, price,
    }));
  }

  static productUploadZipFile({ zip }) {
    return api.post(`${API_URL}/api/products/image`, serialize({ zip }));
  }

  static async getInventory({
    productId, userId, start, end, page, limit, download,
  }) {
    const { data, headers } = await api.get(`${API_URL}/api/products/inventory/search`, {
      params: {
        productId, userId, start, end, page, limit, download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      const [, fileName] = (headers['content-disposition'] || '').match(/\s+filename=(.+)$/) || ['', 'inventory-tracker.xlsx'];

      fileDownload(data, fileName);
    }

    return data;
  }

  static addProductOrder(data) {
    return api.post(`${API_URL}/api/products/orders/add`, data);
  }

  static updateProductOrder(data) {
    return api.put(`${API_URL}/api/products/orders/update`, data);
  }

  static getProductOrder({ orderId, batchId }) {
    return api.get(`${API_URL}/api/products/orders/search`, {
      params: { orderId, batchId },
    });
  }

  static addAdjustProduct(data) {
    return api.post(`${API_URL}/api/products/adjustments/add`, data);
  }

  static getAdjustProduct({ adjustId, batchId }) {
    return api.get(`${API_URL}/api/products/adjustments/search`, {
      params: { adjustId, batchId },
    });
  }

  static updateAdjustProduct(data) {
    return api.put(`${API_URL}/api/products/adjustments/update`, data);
  }

  static updateInventory({ inventoryId, ...data }) {
    return api.put(`${API_URL}/api/products/inventory/${inventoryId}`, data);
  }

  static deleteInventory(inventoryId) {
    return api.delete(`${API_URL}/api/products/inventory/${inventoryId}`);
  }

  static getHealth({
    userId, name, hubId, address, phone, page, limit, healthId, groupId, revenue,
  }) {
    return api.get(`${API_URL}/api/health/search`, {
      params: {
        userId, name, hubId, address, phone, page, limit, healthId, groupId, revenue,
      },
    });
  }

  static addHealth(data) {
    return api.post(`${API_URL}/api/health/add`, data);
  }

  static updateHealth(data) {
    return api.put(`${API_URL}/api/health/update`, data);
  }

  static sendHealthGroup(data) {
    return api.post(`${API_URL}/api/health/group`, data);
  }

  static deleteHealthGroup(data) {
    return api.delete(`${API_URL}/api/health/group`, { data: { ...data } });
  }

  static assignTo({ assign, scriptId }) {
    return api.get(`${API_URL}/api/scripts/assign`, {
      params: { assign, scriptId },
    });
  }

  static uploadRefileFax({
    fileName, faxNumber, scriptId, attachmentFile,
  }) {
    return api.post(
      `${API_URL}/api/faxes/upload`,
      serialize({ attachmentFile }),
      { params: { fileName, faxNumber, scriptId } },
    );
  }

  static scriptFaxUpload(data) {
    return api.put(`${API_URL}/api/scripts/fax`, data);
  }

  static async getSingleReport(body, { page, limit }, name = 'file.xlsx') {
    const { data, headers } = await api.post(`${API_URL}/api/dashboards/report/single`, body, {
      params: { page, limit },
      responseType: body.download ? 'blob' : 'json',
    });

    if (body.download) {
      const [, fileName] = (headers['content-disposition'] || '').match(/\s+filename=(.+)$/) || ['', name];

      fileDownload(data, fileName);
    }

    return data;
  }

  static deleteCard(data) {
    return api.delete(`${API_URL}/api/patients/profile`, { data });
  }

  static createCard(data) {
    return api.post(`${API_URL}/api/patients/profile`, data);
  }

  static mageNotePhysician(data) {
    return api.put(`${API_URL}/api/visits/notes/merge`, data);
  }

  static sendDeliveryPrice(data) {
    return api.post(`${API_URL}/api/delivery/price`, data);
  }

  static possiblePersonsCSVImportFile(file) {
    return api.post(`${API_URL}/api/persons/header`, serialize({ file }));
  }

  static possiblePersonsCSVUploadFile(data) {
    return api.post(`${API_URL}/api/persons/upload`, serialize(data));
  }

  static createPersons(data) {
    return api.post(`${API_URL}/api/persons`, data);
  }

  static getAllPersonsList() {
    return api.get(`${API_URL}/api/persons/data/first`);
  }

  static getPersonsList({ status, page, agent }) {
    return api.get(`${API_URL}/api/persons`, {
      params: {
        status, page, limit: 30, agent,
      },
    });
  }

  static getSinglePersons(id) {
    return api.get(`${API_URL}/api/persons/${id}`);
  }

  static changePersonStatus(data) {
    return api.put(`${API_URL}/api/persons/status/${data.id}`, {
      status: data.status,
      from: _.startCase(data.data.currentItem.parent),
      to: _.startCase(data.data.item.parent),
    });
  }

  static googleOauth() {
    return api.get(`${API_URL}/api/persons/calendar/auth`);
  }

  static googleOauthToken({ code, id }) {
    return api.get(`${API_URL}/api/persons/calendar/auth/token?code=${code}&id=${id}`);
  }

  static updatePerson(data) {
    return api.put(`${API_URL}/api/persons/data/${data.id}`, data);
  }

  static getPersonNotes(personId) {
    return api.get(`${API_URL}/api/persons/notes/${personId}`);
  }

  static assignToRequest(data) {
    return api.put(`${API_URL}/api/persons/responsible`, data);
  }

  static addActivity(data) {
    return api.post(`${API_URL}/api/persons/activity`, data);
  }

  static getActivity({
    id, start, end, page,
  }) {
    return api.get(`${API_URL}/api/persons/activities/${id}`, {
      params: { start, end, page },
    });
  }

  static editActivity(data) {
    return api.put(`${API_URL}/api/persons/activity/${data.id}`, data);
  }

  static markAsDoneActivity({ status, id }) {
    return api.put(`${API_URL}/api/persons/task/${id}?status=${status}`);
  }

  static getChangelog({ id, page }) {
    return api.get(`${API_URL}/api/persons/logs/${id}`, {
      params: { page },
    });
  }

  static getTotalContactsImported({ start, end, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/import`, {
      params: {
        start,
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        healthPlanId,
      },
    });
  }

  static getContacted({ start, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/contacted`, {
      params: { start, healthPlanId },
    });
  }

  static getFollowUp({
    end, start, healthPlanId, status,
  }) {
    return api.get(`${API_URL}/api/persons/statistic/follow`, {
      params: {
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        start,
        healthPlanId,
        status,
      },
    });
  }

  static getActivitiesCompleted({ end, start, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/activity`, {
      params: {
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        start,
        healthPlanId,
      },
    });
  }

  static getPipelineProgress({ end, start, healthPlanId }) {
    return api.get(`${API_URL}/api/persons/statistic/progress`, {
      params: {
        end: end
          ? moment(end).add(1, 'day').format('YYYY-MM-DD')
          : moment(start).add(1, 'day').format('YYYY-MM-DD'),
        start,
        healthPlanId,
      },
    });
  }

  static getChatHealthPlanList({ page, order, orderBy }) {
    return api.get(`${API_URL}/api/health/chat/health-plans`, {
      params: { page, order, orderBy },
    });
  }

  static getChatAssignToList({
    page, order, orderBy, includeAgents,
  }) {
    return api.get(`${API_URL}/api/health/chat/health-plan/assignable/users`, {
      params: {
        page, order, orderBy, includeAgents,
      },
    });
  }

  static assignUserToHealthPlan(data) {
    return api.post(`${API_URL}/api/health/chat/health-plan/assign/user`, data);
  }

  static getChatAssignedUser(params) {
    return api.get(`${API_URL}/api/health/chat/health-plan/assigned/user`, { params });
  }
  // static getChatSelectedHealthPlans({ page, order, orderBy }) {
  //   return api.get(`${chatURL}/api/health/chat/health-plans`, {
  //     params: { page, order, orderBy },
  //   });
  // }

  // static addChatSelectedHealthPlans(id) {
  //   return api.get(`${chatURL}/api/health/chat/health-plans`, {
  //     params: { id },
  //   });
  // }

  static getChatHealthPlanMembersList({
    page, order = 'desc', orderBy = 'roomDate', id, s,
  }) {
    return api.get(`${API_URL}/api/health/chat/health-plan/${id}/members`, {
      params: {
        page, order, orderBy, s,
      },
    });
  }

  static setHealthPlanRoom(data) {
    this.cancel('setHealthPlanRoom');

    return api.post(
      `${API_URL}/api/health/chat/health-plan/set/room`,
      data,
      { cancelToken: this.cancelToken('setHealthPlanRoom') },
    );
  }

  static getHealthPlanChatMessages({
    roomId, messageId, mode, excludeMsg = 1,
  }) {
    this.cancel('getHealthPlanChatMessages');

    return api.get(`${API_URL}/api/health/chat/health-plan/room/messages`, {
      cancelToken: this.cancelToken('getHealthPlanChatMessages'),
      params: {
        roomId, messageId, mode, excludeMsg,
      },
    });
  }

  static sendHealthPlanChatMessage(data) {
    return api.post(`${API_URL}/api/health/chat/send`, data);
  }

  static getMarkedHealthPlans({ page = 1, order = 'desc', orderBy = 'active' }) {
    return api.get(`${API_URL}/api/health/chat/health-plan/marked`, {
      params: { page, order, orderBy },
    });
  }

  static markHealthPlan(id) {
    return api.post(`${API_URL}/api/health/chat/health-plan/mark`, { healthPlanId: id });
  }

  static markHealthPlanActive(id) {
    return api.post(`${API_URL}/api/health/chat/health-plan/mark/active`, { healthPlanId: id });
  }

  static markHealthPlanChatSeen(roomId) {
    return api.get(`${API_URL}/api/health/chat/mark/${roomId}/seen`);
  }

  static searchMessage(data) {
    return api.post(
      `${API_URL}/api/health/chat/health-plan/search/room/message`,
      { ...data, page: 1 },
    );
  }

  static getMessageListByMessageId(messageId) {
    return api.get(`${API_URL}/api/health/chat/health-plan/searched/room/message/${messageId}`);
  }

  static getUnseenCount() {
    return api.get(`${API_URL}/api/health/chat/health-plan/unseen/count`);
  }

  static getRoomData(roomId) {
    return api.get(`${API_URL}/api/health/chat/health-plan/room/${roomId}`);
  }

  static fileUpload(file, roomId) {
    return api.post(`${API_URL}/api/health/chat/file/upload`, serialize({ file, roomId }));
  }

  static getHealthPlanRoom() {
    return api.get(`${API_URL}/api/health/chat/health-plan/room/patient`);
  }

  // ANALYTICS

  static getMessagesCount({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/messages/count`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getAnswersCount({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/answers/count`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getResponseAverageTime({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/response/average/time`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getMessageVolume({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/message/volume/count`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getActiveUsers({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/top/patients`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getActiveAgents({ startDate, endDate, healthPlanId }) {
    return api.get(
      `${API_URL}/api/health/chat/statistics/top/agents`,
      {
        params: { startDate, endDate, healthPlanId },
      },
    );
  }

  static getDriverDeliveries({
    limit, page = 1, start, end, name, sort, sortBy, signed, attachmentId, patientId, physicianId,
  }) {
    return api.get(
      `${API_URL}/api/attachments/driver-data`,
      {
        params: {
          limit, page, start, end, name, sort, sortBy, signed, attachmentId, patientId, physicianId,
        },
      },
    );
  }

  static signDriverDelivery({
    attachmentIds, patientId, userId, signatureFile, physicianId,
  }) {
    return api.put(`${API_URL}/api/attachments/sign-qa-pdf-batch`, serialize({
      attachmentIds, patientId, userId, signatureFile, physicianId,
    }));
  }

  static sendRefund(data) {
    return api.post(`${API_URL}/api/scripts/payments/refund/customer`, data);
  }

  static communicateMDO(data) {
    return api.post(`${API_URL}/api/scripts/communicate-mdo`, data);
  }

  static getFaxes({ scriptId }) {
    return api.get(`${API_URL}/api/faxes/search`, { params: { scriptId } });
  }

  static makeCall(data) {
    return api.post(`${API_URL}/api/goto/create-call-queue`, data);
  }

  static hangupCall(data) {
    return api.post(`${API_URL}/api/goto/hangup`, data);
  }

  // Health Pipeline start
  static getGapDaysSummary({
    start, end, hub, pdcMode,
  }) {
    this.cancel('getGapDaysSummary');

    return api.get(`${API_URL}/api/reports/gap-days/summary`, {
      cancelToken: this.cancelToken('getGapDaysSummary'),
      params: {
        start, end, hub, pdcMode,
      },
    });
  }

  static getGapDays({
    start, end, limit, page, hub, pdcMode,
  }) {
    this.cancel('getGapDays');

    return api.get(`${API_URL}/api/reports/gap-days`, {
      cancelToken: this.cancelToken('getGapDays'),
      params: {
        start, end, limit, page, hub, pdcMode,
      },
    });
  }

  static getGapDaysDistributionPatients({
    start, end, hub, pdcMode, bucket,
  }) {
    this.cancel('getGapDaysDistributionPatients');

    return api.get(`${API_URL}/api/reports/gap-days/distribution/patients`, {
      cancelToken: this.cancelToken('getGapDaysDistributionPatients'),
      params: {
        start, end, hub, pdcMode, bucket,
      },
    });
  }

  static getShortSupplySummary({ start, end, hub }) {
    this.cancel('getShortSupplySummary');

    return api.get(`${API_URL}/api/reports/short-supply/summary`, {
      cancelToken: this.cancelToken('getShortSupplySummary'),
      params: { start, end, hub },
    });
  }

  static getShortSupply({
    start, end, limit, page, hub,
  }) {
    this.cancel('getShortSupply');

    return api.get(`${API_URL}/api/reports/short-supply`, {
      cancelToken: this.cancelToken('getShortSupply'),
      params: {
        start, end, limit, page, hub,
      },
    });
  }

  static getPipelineSummary({ start, end, hub }) {
    this.cancel('getPipelineSummary');

    return api.get(`${API_URL}/api/reports/pipeline-summary`, {
      cancelToken: this.cancelToken('getPipelineSummary'),
      params: { start, end, hub },
    });
  }

  static getPipelineMembers({ start, end, hub }) {
    this.cancel('getPipelineMembers');

    return api.get(`${API_URL}/api/pipeline`, {
      cancelToken: this.cancelToken('getPipelineMembers'),
      params: { start, end, hub },
    });
  }

  static getLeadsSummary({ start, end, hub }) {
    this.cancel('getLeadsSummary');

    return api.get(`${API_URL}/api/reports/leads-summary`, {
      cancelToken: this.cancelToken('getLeadsSummary'),
      params: { start, end, hub },
    });
  }

  static getLeads({
    start, end, limit, hub,
  }) {
    this.cancel('getLeads');

    return api.get(`${API_URL}/api/leads`, {
      cancelToken: this.cancelToken('getLeads'),
      params: {
        start, end, limit, hub,
      },
    });
  }

  static getLeadsFollowUpToday({ hub }) {
    this.cancel('getLeadsFollowUpToday');

    return api.get(`${API_URL}/api/leads/follow-up-today`, {
      cancelToken: this.cancelToken('getLeadsFollowUpToday'),
      params: { hub },
    });
  }

  static getProcessingTimesSummary({ start, end, hub }) {
    this.cancel('getProcessingTimesSummary');

    return api.get(`${API_URL}/api/reports/processing-times/summary`, {
      cancelToken: this.cancelToken('getProcessingTimesSummary'),
      params: { start, end, hub },
    });
  }

  static getProcessingTimes({
    start, end, limit, hub,
  }) {
    this.cancel('getProcessingTimes');

    return api.get(`${API_URL}/api/reports/processing-times`, {
      cancelToken: this.cancelToken('getProcessingTimes'),
      params: {
        start, end, limit, hub,
      },
    });
  }

  static getFillRate90Summary({ start, end, hub }) {
    this.cancel('getFillRate90Summary');

    return api.get(`${API_URL}/api/reports/fill-rate-90/summary`, {
      cancelToken: this.cancelToken('getFillRate90Summary'),
      params: { start, end, hub },
    });
  }

  static getFillRate90({
    start, end, groupBy, limit, hub,
  }) {
    this.cancel('getFillRate90');

    return api.get(`${API_URL}/api/reports/fill-rate-90`, {
      cancelToken: this.cancelToken('getFillRate90'),
      params: {
        start, end, groupBy, limit, hub,
      },
    });
  }

  static getMailOrderConversionsSummary({ start, end, hub }) {
    this.cancel('getMailOrderConversionsSummary');

    return api.get(`${API_URL}/api/reports/mail-order-conversions/summary`, {
      cancelToken: this.cancelToken('getMailOrderConversionsSummary'),
      params: { start, end, hub },
    });
  }

  static getMailOrderConversions({
    start, end, limit, hub,
  }) {
    this.cancel('getMailOrderConversions');

    return api.get(`${API_URL}/api/reports/mail-order-conversions`, {
      cancelToken: this.cancelToken('getMailOrderConversions'),
      params: {
        start, end, limit, hub,
      },
    });
  }

  static getInterventionsReportSummary({ start, end, hub }) {
    this.cancel('getInterventionsReportSummary');

    return api.get(`${API_URL}/api/reports/interventions-summary`, {
      cancelToken: this.cancelToken('getInterventionsReportSummary'),
      params: { start, end, hub },
    });
  }

  static getInterventions({
    start, end, limit, hub,
  }) {
    this.cancel('getInterventions');

    return api.get(`${API_URL}/api/interventions`, {
      cancelToken: this.cancelToken('getInterventions'),
      params: {
        start, end, limit, hub,
      },
    });
  }

  static getInterventionsSummary({ start, end, hub }) {
    this.cancel('getInterventionsSummary');

    return api.get(`${API_URL}/api/interventions/summary`, {
      cancelToken: this.cancelToken('getInterventionsSummary'),
      params: { start, end, hub },
    });
  }

  static getRetentionSummary({ hub }) {
    this.cancel('getRetentionSummary');

    return api.get(`${API_URL}/api/reports/retention/summary`, {
      cancelToken: this.cancelToken('getRetentionSummary'),
      params: { hub },
    });
  }

  static getRetention({
    start, end, limit, hub,
  }) {
    this.cancel('getRetention');

    return api.get(`${API_URL}/api/reports/retention`, {
      cancelToken: this.cancelToken('getRetention'),
      params: {
        start, end, limit, hub,
      },
    });
  }

  static getTransferGaps({ start, end, hub }) {
    this.cancel('getTransferGaps');

    return api.get(`${API_URL}/api/reports/transfer-gaps`, {
      cancelToken: this.cancelToken('getTransferGaps'),
      params: { start, end, hub },
    });
  }

  static getTransfers({
    limit, status, hub,
  }) {
    this.cancel('getTransfers');

    return api.get(`${API_URL}/api/transfers`, {
      cancelToken: this.cancelToken('getTransfers'),
      params: { limit, status, hub },
    });
  }

  static getTransfersStats({ start, end, hub }) {
    this.cancel('getTransfersStats');

    return api.get(`${API_URL}/api/transfers/stats`, {
      cancelToken: this.cancelToken('getTransfersStats'),
      params: { start, end, hub },
    });
  }

  static getJCodesReport({
    start, end, limit, hub,
  }) {
    this.cancel('getJCodesReport');

    return api.get(`${API_URL}/api/reports/jcodes`, {
      cancelToken: this.cancelToken('getJCodesReport'),
      params: {
        start, end, limit, hub,
      },
    });
  }

  static getIehpReport({ start, end, hub }) {
    this.cancel('getIehpReport');

    return api.get(`${API_URL}/api/reports/iehp`, {
      cancelToken: this.cancelToken('getIehpReport'),
      params: { start, end, hub },
    });
  }

  static getIehpSummary({ start, end, hub }) {
    this.cancel('getIehpSummary');

    return api.get(`${API_URL}/api/reports/iehp-summary`, {
      cancelToken: this.cancelToken('getIehpSummary'),
      params: { start, end, hub },
    });
  }

  static getIehpSummaryDetails({
    start, end, hub, lob, finalOutcome, source, page, limit,
  }) {
    this.cancel('getIehpSummaryDetails');

    return api.get(`${API_URL}/api/reports/iehp-summary/details`, {
      cancelToken: this.cancelToken('getIehpSummaryDetails'),
      params: {
        start, end, hub, lob, finalOutcome, source, page, limit,
      },
    });
  }

  static async getIehp({
    page, limit, start, end, sort, sortBy, hub, download,
  }) {
    const cancelKey = download ? 'downloadIehp' : 'getIehp';

    this.cancel(cancelKey);

    const { data, headers } = await api.get(`${API_URL}/api/iehp`, {
      cancelToken: this.cancelToken(cancelKey),
      params: {
        page, limit, start, end, sort, sortBy, hub, download,
      },
      responseType: download ? 'blob' : 'json',
    });

    if (download) {
      const [, fileName] = (headers['content-disposition'] || '').match(/\s+filename=(.+)$/) || ['', 'pipeline.xlsx'];

      fileDownload(data, fileName);
    }

    return { data };
  }

  static addIehp(data) {
    return api.post(`${API_URL}/api/iehp`, data);
  }

  static updateIehp({ id, ...data }) {
    return api.put(`${API_URL}/api/iehp/${id}`, data);
  }

  static deleteIehp({ id }) {
    return api.delete(`${API_URL}/api/iehp/${id}`);
  }

  static getStarAdherenceSummary({
    start, end, hub, pdcMode,
  }) {
    this.cancel('getStarAdherenceSummary');

    return api.get(`${API_URL}/api/reports/star-adherence/summary`, {
      cancelToken: this.cancelToken('getStarAdherenceSummary'),
      params: {
        start, end, hub, pdcMode,
      },
    });
  }

  static getStarAdherence({
    start, end, limit, page, hub, pdcMode,
  }) {
    this.cancel('getStarAdherence');

    return api.get(`${API_URL}/api/reports/star-adherence`, {
      cancelToken: this.cancelToken('getStarAdherence'),
      params: {
        start, end, limit, page, hub, pdcMode,
      },
    });
  }

  // Health Pipeline end
}

export default Api;
